import { useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'

import { useDebouncedSearch } from 'modules/core/hooks/useSearch'

import { useFilterProvider } from 'modules/finance/components/FinancialWorkspace/Providers/FiltersProvider'
import { useFinancialWorkspace } from 'modules/finance/components/FinancialWorkspace/Providers/Provider'
import { useTotal } from 'modules/finance/components/FinancialWorkspace/Providers/TotalProvider'
import { UseAdditionalFilters } from 'modules/finance/components/FinancialWorkspace/types/filter'
import { InsightTableGroupBy } from 'modules/finance/components/FinancialWorkspace/types/table'
import 'modules/finance/components/FinancialWorkspacePayroll/components/Table/const'

import { ExportButton } from './components/ExportButton'
import { GroupBy } from './components/GroupBy'
import { SearchButton } from './components/SearchButton'
import PaginatedTable from './table'
import { DataCollectionTable } from './DataCollectionTable'
import { CustomizableTableProps } from './types'

interface Props {
  tableProps: CustomizableTableProps
  defaultGroupBy: InsightTableGroupBy
  useAdditionalFilters?: UseAdditionalFilters
}

export const Table = ({ tableProps, defaultGroupBy, useAdditionalFilters }: Props) => {
  const { savedFilters } = useFilterProvider()
  const [group, setGroup] = useState<InsightTableGroupBy | undefined>(savedFilters.groupBy)
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const { tableIsEmpty, setTableIsEmpty, setTableIsLoading } = useTotal()

  const { formattedState, availabilities } = useFinancialWorkspace()

  // Feature flag check
  const useOneDataCollection = availabilities.features.finance_workspace_one_data_collection

  const {
    type,
    hideSearch,
    useOnExport,
    headerTitle,
    useDataFetch,
    titleResolver,
    emptyStateProps,
  } = tableProps

  const { filtersApplied: customFiltersApplied, filterTracker: customFilterTracker } =
    useAdditionalFilters?.() || {}

  const onExport = useOnExport?.({ ...formattedState, groupBy: group || defaultGroupBy })?.onExport

  if (!useDataFetch) return null

  // If feature flag is enabled, use the new DataCollectionTable
  if (useOneDataCollection) {
    return (
      <Box paddingTop="s16">
        <Box
          borderRadius={{ all: 'abs016' }}
          border={{ all: { color: 'grey400', width: 's1', style: 'solid' } }}
        >
          <Box flexDirection="row" justifyContent="spaceBetween" gap="s8" padding="s12">
            <Box flexDirection="row" gap="s8" alignItems="center">
              <GroupBy group={group} setGroup={setGroup} tableProps={tableProps} />
              {headerTitle && headerTitle}
            </Box>

            <Box flexDirection="row" gap="s8">
              {!hideSearch?.(group) && <SearchButton search={search || ''} onSearch={onSearch} />}
              {onExport && <ExportButton onExport={onExport} />}
            </Box>
          </Box>

          <DataCollectionTable
            type={type}
            groupBy={group || defaultGroupBy}
            search={debouncedSearch}
          />
        </Box>
      </Box>
    )
  }

  // Original implementation
  return (
    <Box paddingTop="s16">
      <Box
        borderRadius={tableIsEmpty ? undefined : { all: 'abs016' }}
        border={
          tableIsEmpty ? undefined : { all: { color: 'grey400', width: 's1', style: 'solid' } }
        }
      >
        {!tableIsEmpty && (
          <Box flexDirection="row" justifyContent="spaceBetween" gap="s8" padding="s12">
            <Box flexDirection="row" gap="s8" alignItems="center">
              <GroupBy group={group} setGroup={setGroup} tableProps={tableProps} />
              {headerTitle && headerTitle}
            </Box>

            <Box flexDirection="row" gap="s8">
              {!hideSearch?.(group) && <SearchButton search={search || ''} onSearch={onSearch} />}

              {onExport && <ExportButton onExport={onExport} />}
            </Box>
          </Box>
        )}

        <PaginatedTable
          type={type}
          search={debouncedSearch}
          useDataFetch={useDataFetch}
          setIsEmpty={setTableIsEmpty}
          title={titleResolver(group)}
          group={group || defaultGroupBy}
          defaultGroupBy={defaultGroupBy}
          formattedState={formattedState}
          setIsLoading={setTableIsLoading}
          emptyStateProps={emptyStateProps}
          customFilterTracker={customFilterTracker}
          customFiltersApplied={customFiltersApplied}
        />
      </Box>
    </Box>
  )
}
